/* eslint-disable react/jsx-indent */
/* eslint-disable react/jsx-curly-newline */
import { store } from 'redux/store';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMixpanel } from 'react-mixpanel-browser';
import { useDispatch, useSelector } from 'react-redux';

import Page from 'modules/_shared/components/Pages/Page';
import Button from 'modules/_shared/components/Buttons';
import PageListHeader from 'modules/_shared/components/Headers/PageListHeader';
import UploadPartner from 'modules/_shared/components/Modals/UploadPartner';
import EmptyStateCard from 'modules/_shared/components/EmptyStates/EmptyStateCard';
import CustomDataTable from 'modules/_shared/components/Tables/CustomDataTable';
import OperationMenuDots from 'modules/_shared/components/MenuDots/OperationMenuDots';
import { setModal } from 'modules/_shared/redux/modalActions';
import { useGetPartners } from 'modules/partners/hooks/useGetPartners';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { useGetOperationsMenu } from 'modules/transactions/hooks/useGetOperationsMenu';
import { useGetPartnersSectionPermissions } from 'modules/partners/hooks/useGetPartnerSectionsPermissions';
import {
  hasPaginationChanged,
  mapPaginationToPaginationData,
} from 'modules/_shared/components/Pagination/paginationUtils';
import DownloadTableModal from 'modules/_shared/components/Modals/DownloadTableModal';
import { DownloadTableModalProvider } from 'modules/_shared/components/Modals/DownloadTableModal/context/DownloadTableModalContext';

import menuTypes from 'constants/menuTypes';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { SocietyCorporateId } from 'types/Society';
import { usePartnerList } from './hooks/usePartnerList';

const INITIAL_PAGE = 1;
const INITIAL_ROWS_PER_PAGE = 10;

const PartnerList = () => {
  const { t } = useTranslate();
  const mixpanel = useMixpanel();
  const dispatch = useDispatch();
  const { societyId } = useParams();

  const user = useSelector((state: State) => state.user);
  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const partnerCategories = useSelector(
    (state: State) => state.partnerCategories
  );

  const [showNationality, setShowNationality] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [currentPage, setCurrentPage] = useState<number>(INITIAL_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState<number>(INITIAL_ROWS_PER_PAGE);
  const [paginationData, setPaginationData] = useState({
    customItemPerPage: INITIAL_ROWS_PER_PAGE,
    itemPerPage: INITIAL_ROWS_PER_PAGE,
    currentPage: INITIAL_PAGE,
    totalItems: 0,
    totalPages: 0,
  });

  const { data: operationsMenu } = useGetOperationsMenu();

  const { isAdmin, isReadOnly } = useGetPartnersSectionPermissions().data;

  const searchParams = new URLSearchParams(location.href.split('?')[1]);
  const categoryId = searchParams.get('categoria') || '';

  const hasDownload =
    process.env.REACT_APP_UI_CORPORATE_ID !== SocietyCorporateId.AXA;

  const {
    data: { tableData, customData, currentTableColumns },
  } = usePartnerList({
    isAdmin,
    isReadOnly,
    showNationality,
    page: currentPage,
    items: rowsPerPage,
    sortBy: 'date',
    sortOrder: 'desc',
  });

  const { isLoading, isError, pagination } = useGetPartners({
    societyId,
    partnerId: '',
    categoryId,
    page: currentPage,
    items: rowsPerPage,
    searchTerm,
  });

  useGetMenu(menuTypes.EXTENDED);
  useGetActualSociety(societyId);

  const handleChangePage = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsCount: number) => {
    setRowsPerPage(newRowsCount);
  };

  const handleDownloadTable = () => {
    store.dispatch(
      setModal(
        <DownloadTableModalProvider>
          <DownloadTableModal />
        </DownloadTableModalProvider>
      )
    );
  };

  const handleClickUploadButton = () => {
    dispatch(
      setModal(
        <UploadPartner
          // @ts-ignore
          user={user}
          societyId={actualSociety['_id']}
        />
      )
    );
  };

  const handleClickOperationButton = (Modal: any) => {
    dispatch(setModal(<Modal user={user} societyId={actualSociety._id} />));
  };

  useEffect(() => {
    if (pagination && hasPaginationChanged(pagination, paginationData)) {
      setPaginationData((prevState) => ({
        ...prevState,
        ...mapPaginationToPaginationData(pagination),
      }));
    }
  }, [pagination]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentPage(INITIAL_PAGE);
  }, [searchTerm]);

  const getCategoryLabel = (categoryId: string) => {
    if (!partnerCategories || !categoryId) return '';

    const category = partnerCategories.find((item) => item._id === categoryId);

    if (!category) return '';

    return category?.isDefault ? t(category?.name) : category.name;
  };

  const getPageDescription = () => (
    <p>
      {actualSociety?.name &&
        `${actualSociety.name || ''} ${t('HavePartners', {
          count: actualSociety?.partnersCount?.active || 0,
        })}`}
    </p>
  );

  return (
    <Page id="partners-list-page">
      <PageListHeader
        title={actualSociety?.name}
        subTitle={t('Partners')}
        subTitleSuffix={getCategoryLabel(categoryId)}
        description={getPageDescription()}
        actions={
          isAdmin
            ? [
                <Button
                  variant="light"
                  icon="upload-cloud"
                  text={t('UploadListing')}
                  onClick={handleClickUploadButton}
                  disabled={isReadOnly}
                  outlineStyle
                />,
                <OperationMenuDots
                  menuOptions={operationsMenu}
                  handleModal={handleClickOperationButton}
                  params={{
                    society: actualSociety,
                    user,
                    mixpanel,
                  }}
                  disabled={isReadOnly}
                />,
              ]
            : []
        }
      />

      {isError && !isLoading && (
        <EmptyStateCard
          mainMessage={t('ErrorLoadingPartnersList')}
          infoMessage={t('ErrorLoadingPartnersListMessage')}
          bordered={false}
        />
      )}
      <CustomDataTable
        data={tableData}
        columns={currentTableColumns}
        searchBy={[t('Partner'), t('Email'), t('CIF')]}
        searchByPlaceholder={[t('name'), t('email'), t('CIF')].join()}
        defaultSortFieldId={6}
        defaultSortAsc={false}
        sortServer
        pagination
        className="nk-tb-list"
        expandableRows={false}
        actions={isAdmin}
        showDense
        showDownload={isAdmin && hasDownload}
        showNationality={isAdmin}
        toggleNationality={() =>
          isAdmin ? setShowNationality(!showNationality) : undefined
        }
        customData={customData}
        filterByParam={isAdmin ? 'isActive' : undefined}
        filterText={
          isAdmin ? { true: t('SeeOld'), false: t('HideOld') } : undefined
        }
        filterIcon={
          isAdmin ? { true: 'ni ni-eye', false: 'ni ni-eye-off' } : undefined
        }
        onFilterChange={setSearchTerm}
        fileName={`${t('PartnersList')} ${actualSociety?.name || ''}`}
        isLoading={isLoading}
        // PAGINATION
        hasCustomPagination
        customPagination={{
          className: 'bg-white',
          totalItems: paginationData.totalItems,
          itemPerPage: paginationData.itemPerPage,
          currentPage: paginationData.currentPage,
          customItemPerPage: paginationData.customItemPerPage,
          onChangePage: handleChangePage,
          onChangeRowsPerPage: handleChangeRowsPerPage,
          setRowsPerPage,
        }}
        onDownloadTable={handleDownloadTable}
      />
    </Page>
  );
};

export default PartnerList;
